import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAutomation,
  createAutomation,
  updateAutomation,
  TriggerType,
  EventType,
  ActionType,
  ConditionOperator,
  type CreateAutomationInput,
  type Condition,
  type Action,
} from '../services/automationService';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const AutomationBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<TriggerType>(TriggerType.EVENT);
  const [eventType, setEventType] = useState<EventType>(EventType.POST_PUBLISHED);
  const [cronSchedule, setCronSchedule] = useState('0 9 * * *');
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEditMode) {
      loadAutomation();
    }
  }, [id]);

  const loadAutomation = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const automation = await getAutomation(id);
      setName(automation.name);
      setDescription(automation.description || '');
      setTriggerType(automation.triggerType);
      
      if (automation.triggerConfig.eventType) {
        setEventType(automation.triggerConfig.eventType);
      }
      if (automation.triggerConfig.cronSchedule) {
        setCronSchedule(automation.triggerConfig.cronSchedule);
      }
      
      setConditions(automation.conditions);
      setActions(automation.actions);
      setIsActive(automation.isActive);
    } catch (error) {
      console.error('Failed to load automation:', error);
      alert('Failed to load automation');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a name for this automation');
      return;
    }

    if (actions.length === 0) {
      alert('Please add at least one action');
      return;
    }

    const input: CreateAutomationInput = {
      name,
      description,
      triggerType,
      triggerConfig: {
        type: triggerType,
        ...(triggerType === TriggerType.EVENT && { eventType }),
        ...(triggerType === TriggerType.SCHEDULE && { cronSchedule, timezone: 'UTC' }),
      },
      conditions,
      actions,
      isActive,
    };

    try {
      setSaving(true);
      if (isEditMode && id) {
        await updateAutomation(id, input);
      } else {
        await createAutomation(input);
      }
      navigate('/automations');
    } catch (error: any) {
      console.error('Failed to save automation:', error);
      alert(error.response?.data?.error || 'Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  const addCondition = () => {
    setConditions([
      ...conditions,
      {
        field: 'membership.tier',
        operator: ConditionOperator.EQUALS,
        value: '',
      },
    ]);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setConditions(newConditions);
  };

  const addAction = () => {
    setActions([
      ...actions,
      {
        type: ActionType.SEND_MESSAGE,
        config: { message: '' },
      },
    ]);
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const updateAction = (index: number, updates: Partial<Action>) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], ...updates };
    setActions(newActions);
  };

  const updateActionConfig = (index: number, key: string, value: any) => {
    const newActions = [...actions];
    newActions[index] = {
      ...newActions[index],
      config: { ...newActions[index].config, [key]: value },
    };
    setActions(newActions);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/automations')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Automations
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Automation' : 'Create Automation'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Set up triggers, conditions, and actions to automate your workflow
        </p>
      </div>

      <div className="space-y-8">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Automation Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g., Welcome new patrons"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Brief description of what this automation does"
              />
            </div>
          </div>
        </div>

        {/* Trigger */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Trigger</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                When should this automation run?
              </label>
              <select
                value={triggerType}
                onChange={(e) => setTriggerType(e.target.value as TriggerType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value={TriggerType.EVENT}>When an event occurs</option>
                <option value={TriggerType.SCHEDULE}>On a schedule</option>
                <option value={TriggerType.MANUAL}>Manual trigger only</option>
              </select>
            </div>

            {triggerType === TriggerType.EVENT && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Event
                </label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value as EventType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <optgroup label="Membership Events">
                    <option value={EventType.MEMBERSHIP_CREATED}>New patron subscribed</option>
                    <option value={EventType.MEMBERSHIP_UPDATED}>Patron updated membership</option>
                    <option value={EventType.MEMBERSHIP_CANCELLED}>Patron cancelled</option>
                    <option value={EventType.MEMBERSHIP_RENEWED}>Subscription renewed</option>
                  </optgroup>
                  <optgroup label="Payment Events">
                    <option value={EventType.PAYMENT_SUCCEEDED}>Payment succeeded</option>
                    <option value={EventType.PAYMENT_FAILED}>Payment failed</option>
                    <option value={EventType.PAYMENT_REFUNDED}>Payment refunded</option>
                  </optgroup>
                  <optgroup label="Content Events">
                    <option value={EventType.POST_PUBLISHED}>Post published</option>
                    <option value={EventType.POST_UPDATED}>Post updated</option>
                    <option value={EventType.POST_DELETED}>Post deleted</option>
                  </optgroup>
                  <optgroup label="Engagement Events">
                    <option value={EventType.COMMENT_CREATED}>New comment</option>
                    <option value={EventType.LIKE_CREATED}>New like</option>
                    <option value={EventType.FOLLOW_CREATED}>New follower</option>
                  </optgroup>
                </select>
              </div>
            )}

            {triggerType === TriggerType.SCHEDULE && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cron Schedule
                </label>
                <input
                  type="text"
                  value={cronSchedule}
                  onChange={(e) => setCronSchedule(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0 9 * * * (9 AM daily)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Format: minute hour day month weekday (e.g., "0 9 * * *" for 9 AM daily)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Conditions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Conditions (Optional)</h2>
              <p className="text-sm text-gray-600">Add conditions to filter when actions run</p>
            </div>
            <button
              onClick={addCondition}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Condition
            </button>
          </div>

          {conditions.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              No conditions added. This automation will run for all trigger events.
            </div>
          ) : (
            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={condition.field}
                    onChange={(e) => updateCondition(index, { field: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Field (e.g., membership.tier)"
                  />
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, { operator: e.target.value as ConditionOperator })}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value={ConditionOperator.EQUALS}>equals</option>
                    <option value={ConditionOperator.NOT_EQUALS}>not equals</option>
                    <option value={ConditionOperator.GREATER_THAN}>greater than</option>
                    <option value={ConditionOperator.LESS_THAN}>less than</option>
                    <option value={ConditionOperator.CONTAINS}>contains</option>
                    <option value={ConditionOperator.NOT_CONTAINS}>not contains</option>
                  </select>
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Value"
                  />
                  <button
                    onClick={() => removeCondition(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Actions *</h2>
              <p className="text-sm text-gray-600">What should happen when this automation runs?</p>
            </div>
            <button
              onClick={addAction}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Action
            </button>
          </div>

          {actions.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
              No actions added yet. Click "Add Action" to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {actions.map((action, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Action {index + 1}
                    </label>
                    <button
                      onClick={() => removeAction(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <select
                      value={action.type}
                      onChange={(e) => updateAction(index, { type: e.target.value as ActionType })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value={ActionType.SEND_MESSAGE}>Send direct message</option>
                      <option value={ActionType.SEND_EMAIL}>Send email</option>
                      <option value={ActionType.TRIGGER_WEBHOOK}>Trigger webhook</option>
                      <option value={ActionType.TRIGGER_INTEGRATION}>Trigger integration</option>
                      <option value={ActionType.CREATE_POST}>Create a post</option>
                      <option value={ActionType.ADD_TAG}>Add patron tag</option>
                    </select>

                    {(action.type === ActionType.SEND_MESSAGE || action.type === ActionType.SEND_EMAIL) && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {action.type === ActionType.SEND_EMAIL ? 'Subject' : 'Message'}
                        </label>
                        {action.type === ActionType.SEND_EMAIL && (
                          <input
                            type="text"
                            value={action.config.subject || ''}
                            onChange={(e) => updateActionConfig(index, 'subject', e.target.value)}
                            className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Email subject"
                          />
                        )}
                        <textarea
                          value={action.config.message || ''}
                          onChange={(e) => updateActionConfig(index, 'message', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder={`Enter your ${action.type === ActionType.SEND_EMAIL ? 'email' : 'message'} here...`}
                        />
                      </div>
                    )}

                    {action.type === ActionType.TRIGGER_INTEGRATION && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Integration ID
                        </label>
                        <input
                          type="text"
                          value={action.config.integrationId || ''}
                          onChange={(e) => updateActionConfig(index, 'integrationId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Enter integration ID"
                        />
                      </div>
                    )}

                    {action.type === ActionType.ADD_TAG && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tag Name
                        </label>
                        <input
                          type="text"
                          value={action.config.tag || ''}
                          onChange={(e) => updateActionConfig(index, 'tag', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Enter tag name"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Automation Status</h3>
              <p className="text-sm text-gray-600">
                {isActive ? 'This automation is active and will run automatically' : 'This automation is paused and will not run'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`${
                isActive ? 'bg-indigo-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  isActive ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => navigate('/automations')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Automation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomationBuilder;
